import { CountUp, formatClock, formatInt, nightWindow } from '../index.js';

import type { StyleWithVars } from '../index.js';
import type { PartyWrapped, WrappedStint } from '@mixwire/contracts';
import type { ReactElement } from 'react';

type Slide07TimelineProps = {
  readonly wrapped: PartyWrapped;
};

/** A block has to own more than this slice of the night before a few characters of name fit in it. */
const NAME_WIDTH_FRACTION = 0.08;
/** Narrower than this and a block is a hairline, not something anyone can read. */
const SLIVER_FRACTION = 0.01;
/** The same DJ back within this slice of the night reads as one run, not a stutter of blocks. */
const MERGE_GAP_FRACTION = 0.01;
const LIGHT_UP_FROM_MS = 400;
const LIGHT_UP_SPAN_MS = 3100;

const clampFraction = (value: number): number => Math.min(Math.max(value, 0), 1);

type Segment = {
  readonly name: string;
  readonly start: number;
  readonly end: number;
};

type Block = {
  readonly key: string;
  readonly name: string;
  readonly left: number;
  readonly width: number;
};

const segmentOf = (stint: WrappedStint, startMs: number, endMs: number): Segment => {
  const spanMs = Math.max(endMs - startMs, 1);
  const stintEndMs = stint.endedAt === null ? endMs : Date.parse(stint.endedAt);

  return {
    name: stint.displayName,
    start: clampFraction((Date.parse(stint.startedAt) - startMs) / spanMs),
    end: clampFraction((stintEndMs - startMs) / spanMs),
  };
};

const clipToNext = (segments: readonly Segment[]): readonly Segment[] =>
  segments.map((segment, index) => {
    const next = segments[index + 1];

    return next === undefined ? segment : { ...segment, end: Math.min(segment.end, next.start) };
  });

const isSameRun = (run: Segment, segment: Segment): boolean =>
  run.name === segment.name && segment.start - run.end <= MERGE_GAP_FRACTION;

const mergeSameName = (segments: readonly Segment[]): readonly Segment[] => {
  const [run, segment, ...rest] = segments;

  if (run === undefined || segment === undefined) {
    return segments;
  }

  return isSameRun(run, segment)
    ? mergeSameName([{ ...run, end: Math.max(run.end, segment.end) }, ...rest])
    : [run, ...mergeSameName([segment, ...rest])];
};

const isVisible = (segment: Segment): boolean => segment.end - segment.start >= SLIVER_FRACTION;

const byStartThenEnd = (a: Segment, b: Segment): number =>
  a.start === b.start ? a.end - b.end : a.start - b.start;

const laneSegments = (segments: readonly Segment[]): readonly Segment[] => {
  const clipped = clipToNext(segments.toSorted(byStartThenEnd));

  return mergeSameName(mergeSameName(clipped).filter(isVisible));
};

const blocksOnSlot = (
  stints: readonly WrappedStint[],
  slotNumber: number,
  startMs: number,
  endMs: number,
): readonly Block[] =>
  laneSegments(
    stints
      .filter((stint) => stint.slotNumber === slotNumber)
      .map((stint) => segmentOf(stint, startMs, endMs)),
  ).map((segment) => ({
    key: `${String(slotNumber)}-${String(segment.start)}`,
    name: segment.name,
    left: segment.start,
    width: segment.end - segment.start,
  }));

export const Slide07Timeline = ({ wrapped }: Slide07TimelineProps): ReactElement => {
  const { startMs, endMs } = nightWindow(wrapped);

  const axisStyle: StyleWithVars = { '--d': '300' };
  const closingStyle: StyleWithVars = { '--d': '3400' };

  return (
    <>
      <h2 className="big rise">Three channels, one night</h2>

      <div className="tl">
        {wrapped.channels.map((channel) => (
          <div
            className="lane"
            key={channel.slotNumber}
            aria-label={`Channel ${String(channel.slotNumber)}`}
          >
            {blocksOnSlot(wrapped.stints, channel.slotNumber, startMs, endMs).map((block) => {
              const blockStyle: StyleWithVars = {
                '--d': String(Math.round(LIGHT_UP_FROM_MS + block.left * LIGHT_UP_SPAN_MS)),
                left: `${String(block.left * 100)}%`,
                width: `${String(block.width * 100)}%`,
                background: channel.colour,
              };

              return (
                <span className="stint grow" key={block.key} style={blockStyle} title={block.name}>
                  {block.width > NAME_WIDTH_FRACTION ? (
                    <span className="stint-name">{block.name}</span>
                  ) : null}
                </span>
              );
            })}
          </div>
        ))}
        <span className="playhead" aria-hidden="true" />
      </div>

      <div className="axis rise" style={axisStyle}>
        <span>{formatClock(new Date(startMs))}</span>
        <span>{formatClock(new Date(endMs))}</span>
      </div>

      {wrapped.handovers === 0 ? (
        <p className="lede rise" style={closingStyle}>
          The decks never changed hands.
        </p>
      ) : (
        <p className="lede rise" style={closingStyle}>
          The decks changed hands{' '}
          <b>
            <CountUp to={wrapped.handovers} format={formatInt} delayMs={3600} />
          </b>{' '}
          {wrapped.handovers === 1 ? 'time' : 'times'}.
        </p>
      )}
    </>
  );
};
